/**
 * Pure helpers for the sidebar's layout decisions, kept out of the component so
 * they are unit-tested rather than eyeballed in a browser.
 */

/**
 * Node labels that belong to operator tooling rather than creative work. Matched
 * on the menuData label so this stays readable next to the menu tree itself.
 */
const ADMIN_LABELS = new Set([
  'Administration',
  'Admin',
  'Platform Administration',
  'Compliance & Audit',
  'Models & Routing',
  'Settings',
  // Fleet health, job queues and workflow plumbing are operator tooling. In
  // the creative list they sat between "Brand & Identity" and "Review" and
  // read as things a producer was expected to use.
  'Command Center',
  'Workflows',
]);

/**
 * True when a menu node is administrative — either by label, or because every
 * way into it is gated behind a role. A node the whole team can reach is not
 * administration however it is named.
 */
export function isAdminNode(node) {
  if (!node) return false;
  if (node.section === 'primary') return false; // explicit opt-out: role-gated but not administration
  if (ADMIN_LABELS.has(node.label)) return true;
  const roles = node.requiredRoles;
  return Array.isArray(roles) && roles.length > 0;
}

/**
 * Splits the visible nav into the creative sections and the administrative
 * ones, preserving menuData's order within each. Administration sinks to the
 * foot of the rail so operator tooling never interrupts the creative sequence.
 */
export function partitionNav(nodes = []) {
  const primary = [];
  const admin = [];
  for (const node of nodes) (isAdminNode(node) ? admin : primary).push(node);
  return { primary, admin };
}

/** localStorage key for the rail's collapsed state. */
export const NAV_COLLAPSED_KEY = 'psos.nav.collapsed';

/**
 * Reads the persisted collapse state. Generator screens (Generation Studio,
 * Property Bibles) want the horizontal room, and that preference should survive
 * a reload rather than resetting every time you navigate.
 *
 * Storage can throw (private mode, disabled cookies) — a nav must never fail
 * to render because a preference could not be read.
 */
export function readNavCollapsed(storage) {
  try {
    return storage?.getItem(NAV_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

/** Persists the collapse state, ignoring storage failures for the same reason. */
export function writeNavCollapsed(storage, collapsed) {
  // Optional chaining would silently no-op and still report success, so the
  // absence of storage is checked explicitly — "wrote nothing" is not "wrote".
  if (!storage?.setItem) return false;
  try {
    storage.setItem(NAV_COLLAPSED_KEY, collapsed ? '1' : '0');
    return true;
  } catch {
    return false;
  }
}

export default { isAdminNode, partitionNav, readNavCollapsed, writeNavCollapsed, NAV_COLLAPSED_KEY };
